'use client';
import { useState, useEffect, useRef } from 'react';
import type { WizardCtx } from '../types';
import { StepLayout, PrimaryBtn } from '../shared';
import { api, ApiError, useAuth, toast } from '@splace/core';
import { useRouter } from 'next/navigation';

export function VerifyStep({ ctx }: { ctx: WizardCtx }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [seconds, setSeconds] = useState(42);
  const [loading, setLoading] = useState(false);
  const setUser = useAuth(s => s.setUser);
  const router = useRouter();

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  useEffect(() => { refs.current[0]?.focus(); }, []);

  function handleChange(i: number, v: string) {
    const ch = v.replace(/[^0-9]/g, '').slice(-1);
    const c = [...code]; c[i] = ch; setCode(c);
    if (ch && i < 5) refs.current[i + 1]?.focus();
  }

  function handleKey(i: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !code[i] && i > 0) refs.current[i - 1]?.focus();
  }

  function handlePaste(e: React.ClipboardEvent) {
    const txt = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const arr = txt.split('').concat(['', '', '', '', '', '']).slice(0, 6);
    setCode(arr);
    if (txt.length === 6) setTimeout(() => verify(txt), 250);
    else refs.current[txt.length]?.focus();
  }

  const filled = code.every(c => c !== '');

  async function verify(codeStr?: string) {
    const c = codeStr ?? code.join('');
    if (c.length < 6 || loading) return;
    setLoading(true);
    try {
      const result = await api.auth.verifyOtp(ctx.data.email, c);

      // User is always created at this point — set them in store
      setUser(result.user);

      if (!result.isNewUser) {
        toast.success('Welcome back!');
        router.push('/feed');
      } else {
        toast.success('Email verified — complete your profile');
        ctx.next();
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Invalid code');
      setCode(['', '', '', '', '', '']);
      refs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }

  async function resend() {
    try {
      await api.auth.sendOtp(ctx.data.email);
      setSeconds(42);
      toast.info('New code sent');
    } catch {
      toast.error('Failed to resend');
    }
  }

  return (
    <StepLayout
      narrow
      eyebrow="STEP 02 · VERIFY"
      title="Check your inbox"
      sub={<>We sent a 6-digit code to <strong>{ctx.data.email || 'your email'}</strong>. Drop it below.</>}
    >
      <div className="ob-otp" onPaste={handlePaste}>
        {code.map((c, i) => (
          <input
            key={i}
            ref={el => { refs.current[i] = el; }}
            className="ob-otp-cell" type="text" inputMode="numeric" maxLength={1}
            value={c}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKey(i, e)}
            disabled={loading}
          />
        ))}
      </div>

      <div className="ob-otp-meta">
        {seconds > 0
          ? <span className="ob-fineprint">Resend code in <span className="sp-mono">0:{String(seconds).padStart(2, '0')}</span></span>
          : <button className="ob-link-btn" onClick={resend}>Resend code</button>}
      </div>

      <PrimaryBtn full onClick={() => verify()} disabled={!filled || loading}>
        {loading ? 'Verifying…' : 'Verify and continue'}
      </PrimaryBtn>

      <p className="ob-fineprint" style={{ marginTop: 24 }}>
        Wrong email? <a href="#" onClick={e => { e.preventDefault(); ctx.back(); }}>Edit it</a>.
      </p>
    </StepLayout>
  );
}
